import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { PRODUCTS as STATIC_PRODUCTS, SETS as STATIC_SETS } from '../data/products';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fetches product overrides + custom products + set overrides in parallel
// and merges them on top of the hardcoded base data so every page renders the
// up-to-date catalogue without code changes.

let _cache = null;
let _pending = null;

const fetchAll = async () => {
  if (_cache) return _cache;
  if (!_pending) {
    _pending = Promise.all([
      axios.get(`${API}/products`).then((r) => r.data || {}).catch(() => ({})),
      axios.get(`${API}/sets`).then((r) => r.data || {}).catch(() => ({})),
    ])
      .then(([products, sets]) => {
        const productsShape = Array.isArray(products)
          ? { custom: products, overrides: {} }
          : { custom: products.custom || [], overrides: products.overrides || {} };
        _cache = {
          ...productsShape,
          setOverrides: sets.overrides || {},
        };
        return _cache;
      })
      .finally(() => { _pending = null; });
  }
  return _pending;
};

export const invalidateProducts = () => { _cache = null; };

export const useProducts = () => {
  const [data, setData] = useState(_cache || { custom: [], overrides: {}, setOverrides: {} });
  const [loading, setLoading] = useState(!_cache);

  const refetch = useCallback(async () => {
    invalidateProducts();
    setLoading(true);
    const fresh = await fetchAll();
    setData(fresh);
    setLoading(false);
    return fresh;
  }, []);

  useEffect(() => {
    if (_cache) return;
    let mounted = true;
    fetchAll().then((d) => {
      if (mounted) { setData(d); setLoading(false); }
    });
    return () => { mounted = false; };
  }, []);

  const { custom, overrides, setOverrides } = data;
  const overrideCodes = new Set(Object.keys(overrides));
  const setOverrideSlugs = new Set(Object.keys(setOverrides));
  const customCodes = new Set(custom.map((p) => p.code));

  // Merge product map: base + overrides applied + customs appended
  const merged = {};
  for (const [code, base] of Object.entries(STATIC_PRODUCTS)) {
    merged[code] = overrideCodes.has(code) ? { ...base, ...overrides[code] } : base;
  }
  for (const p of custom) {
    if (!merged[p.code]) merged[p.code] = { ...p };
  }

  // Public-facing product list: excludes draft (visible === false) products.
  // Admin pages should use `products` directly to see everything.
  const visibleProducts = Object.fromEntries(
    Object.entries(merged).filter(([, p]) => p.visible !== false)
  );

  // Merge sets: apply overrides + add custom items that opted in
  const mergedSets = STATIC_SETS.map((s) => {
    let next = setOverrideSlugs.has(s.slug) ? { ...s, ...setOverrides[s.slug] } : s;
    // Append custom products that selected this set, but only if the set's
    // items list wasn't explicitly overridden (otherwise the admin's items
    // wins).
    if (!setOverrideSlugs.has(s.slug) || !setOverrides[s.slug]?.items) {
      const fromCustom = custom
        .filter((p) => Array.isArray(p.set_slugs) && p.set_slugs.includes(s.slug))
        .map((p) => p.code);
      if (fromCustom.length) {
        const items = Array.from(new Set([...next.items, ...fromCustom]));
        next = { ...next, items };
      }
    }
    return { ...next, pieceCount: next.items.length };
  });

  return {
    products: merged,
    visibleProducts,
    custom,
    overrides,
    setOverrides,
    isCustom: (code) => customCodes.has(code),
    hasOverride: (code) => overrideCodes.has(code),
    hasSetOverride: (slug) => setOverrideSlugs.has(slug),
    sets: mergedSets,
    loading,
    refetch,
  };
};

export default useProducts;
